// Company holidays (PRD §2.2, EC5). The real list is admin-edited per year. This generates the
// Employee Handbook's list (Part 5, Holiday Pay; rev. 1.16) used to seed a year, and to cost a year
// nobody has entered yet (flagged as an estimate on every rate that uses it).

import { addDays, dayOfWeek, isIsoDate, isWeekday, type Iso } from "./dates";

export interface Holiday {
  date: Iso;
  name: string;
}

const iso = (year: number, month: number, day: number): Iso =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** The nth given weekday (0 = Sunday) of a month. */
function nthWeekday(year: number, month: number, weekday: number, n: number): Iso {
  const first = iso(year, month, 1);
  const offset = (weekday - dayOfWeek(first) + 7) % 7;
  return addDays(first, offset + (n - 1) * 7);
}

function lastWeekday(year: number, month: number, weekday: number): Iso {
  const nextFirst = month === 12 ? iso(year + 1, 1, 1) : iso(year, month + 1, 1);
  const last = addDays(nextFirst, -1);
  return addDays(last, -((dayOfWeek(last) - weekday + 7) % 7));
}

/** A fixed-date holiday on a weekend is observed on the nearest weekday: Saturday → Friday, Sunday → Monday. */
function observed(day: Iso): Iso {
  const d = dayOfWeek(day);
  return d === 6 ? addDays(day, -1) : d === 0 ? addDays(day, 1) : day;
}

/** Easter Sunday (Gregorian, the anonymous algorithm). */
function easter(year: number): Iso {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(year, month, day);
}

/** The weekday before a day. */
function weekdayBefore(day: Iso): Iso {
  let d = addDays(day, -1);
  while (!isWeekday(d)) d = addDays(d, -1);
  return d;
}

/**
 * The handbook's ten paid holidays, observed dates: New Year's Day, Presidents Day, Good Friday,
 * Memorial Day, Independence Day, Labor Day, Thanksgiving and the day after, Christmas Eve and
 * Christmas Day. Christmas Eve is the weekday before the observed Christmas, so the two never fall on
 * one day. A starting point for an admin to confirm each year.
 */
export function standardHolidays(year: number): Holiday[] {
  const thanksgiving = nthWeekday(year, 11, 4, 4);
  const christmas = observed(iso(year, 12, 25));
  return [
    { date: observed(iso(year, 1, 1)), name: "New Year's Day" },
    { date: nthWeekday(year, 2, 1, 3), name: "Presidents' Day" },
    { date: addDays(easter(year), -2), name: "Good Friday" },
    { date: lastWeekday(year, 5, 1), name: "Memorial Day" },
    { date: observed(iso(year, 7, 4)), name: "Independence Day" },
    { date: nthWeekday(year, 9, 1, 1), name: "Labor Day" },
    { date: thanksgiving, name: "Thanksgiving" },
    { date: addDays(thanksgiving, 1), name: "Day after Thanksgiving" },
    { date: weekdayBefore(christmas), name: "Christmas Eve" },
    { date: christmas, name: "Christmas Day" },
  ];
}

/** A stored year of the company calendar. Seeded from the standard list; an admin confirms it. */
export interface HolidayYearDoc {
  year: number;
  days: Holiday[];
  confirmed: boolean;
  confirmedBy?: string;
  confirmedAt?: string;
}

export function validateHolidays(doc: Partial<HolidayYearDoc>): string[] {
  const errors: string[] = [];
  if (!Number.isInteger(doc.year)) errors.push("The list needs a year.");
  if (!Array.isArray(doc.days)) return [...errors, "The list of days is missing."];
  const seen = new Set<string>();
  for (const h of doc.days) {
    if (!isIsoDate(h?.date)) errors.push("Every holiday needs a valid date.");
    else if (seen.has(h.date)) errors.push(`${h.date} is listed twice.`);
    else seen.add(h.date);
    if (typeof h?.name !== "string" || !h.name.trim() || h.name.length > 80) errors.push("Every holiday needs a short name.");
  }
  return errors;
}

/** Holidays that fall on a weekday in `year`: the ones that reduce productive hours. */
export function weekdayHolidaysIn(year: number, dates: Iterable<Iso>): number {
  const prefix = `${year}-`;
  const seen = new Set<Iso>();
  for (const d of dates) if (d.startsWith(prefix) && isWeekday(d)) seen.add(d);
  return seen.size;
}
